import React, { useState, useEffect } from 'react'
import { Form } from 'react-bootstrap'
import { usePost, notify } from '../../../../components/component-items/helpers'
import GenericModal from '../../../../components/component-items/modal'
import GlobalStyles from '../../../../components/component-items/styles'

import type { MerchantUserOverride, BusinessSegment, FunctionRole } from '../../../../types/rbac'
import { ALL_SEGMENTS, SEGMENT_LABELS, ALL_LEVELS, LEVEL_LABELS } from '../../../../types/rbac'

interface MerchantOverrideModalProps {
  show: boolean
  onHide: () => void
  onSaved: () => void
  userId: number
  existingOverride: MerchantUserOverride | null
  companyId: number | null
  csrf: string
}

export const MerchantOverrideModal = ({
  show,
  onHide,
  onSaved,
  userId,
  existingOverride,
  companyId,
  csrf,
}: MerchantOverrideModalProps) => {
  const [segment, setSegment] = useState<BusinessSegment | ''>('')
  const [level, setLevel] = useState<FunctionRole | ''>('')
  const [reason, setReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Reset form when modal opens/closes or when editing different override
  useEffect(() => {
    if (show) {
      if (existingOverride) {
        setSegment(existingOverride.segment)
        setLevel(existingOverride.access_level)
        setReason(existingOverride.reason || '')
      } else {
        setSegment('')
        setLevel('')
        setReason('')
      }
    }
  }, [show, existingOverride])

  const handleSubmit = async () => {
    if (!segment) {
      notify({ type: 'error', message: 'Please select a segment' })
      return
    }

    if (!level) {
      notify({ type: 'error', message: 'Please select an access level' })
      return
    }

    if (!reason.trim()) {
      notify({ type: 'error', message: 'Please provide a reason for this override' })
      return
    }

    setIsSubmitting(true)

    try {
      const payload = {
        operations: [
          {
            segment,
            level,
            reason: reason.trim(),
            company_id: companyId,
          },
        ],
      }

      const resp = await usePost(
        `/api/rbac/users/${userId}/overrides/bulk/`,
        payload,
        csrf,
        false,
        true,
        true,
        true,
        false
      )

      if (resp?.data) {
        const operation = resp.data.operations?.[0]
        if (operation?.status === 'error') {
          notify({ type: 'error', message: operation.message || 'Failed to save override' })
        } else {
          notify({ type: 'success', message: existingOverride ? 'Override updated' : 'Override created' })
          onSaved()
        }
      } else {
        notify({ type: 'error', message: 'Failed to save override' })
      }
    } catch (error) {
      notify({ type: 'error', message: 'Failed to save override' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <GenericModal
      heading={existingOverride ? 'Edit Override' : 'Add Override'}
      show={show}
      onHide={onHide}
      buttons={
        <>
          <GlobalStyles.Button className="secondary" onClick={onHide} disabled={isSubmitting}>
            Cancel
          </GlobalStyles.Button>
          <GlobalStyles.Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save'}
          </GlobalStyles.Button>
        </>
      }
    >
      <Form style={{ margin: '1.5em' }}>
        {/* 1. Permission Section */}
        <div style={{ marginBottom: '1.5em' }}>
          <h6 style={{ marginBottom: '1em', color: '#48465b' }}>1. Permission</h6>

          <Form.Group className="required" style={{ marginBottom: '1em' }}>
            <Form.Label>Segment</Form.Label>
            <Form.Select
              value={segment}
              onChange={(e) => setSegment(e.target.value as BusinessSegment)}
              disabled={!!existingOverride} // Can't change segment when editing
            >
              <option value="">Select a segment...</option>
              {ALL_SEGMENTS.map((seg) => (
                <option key={seg} value={seg}>
                  {SEGMENT_LABELS[seg]}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="required">
            <Form.Label>Access Level</Form.Label>
            <Form.Select value={level} onChange={(e) => setLevel(e.target.value as FunctionRole)}>
              <option value="">Select a level...</option>
              {ALL_LEVELS.map((lvl) => (
                <option key={lvl} value={lvl}>
                  {LEVEL_LABELS[lvl]}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>

        {/* 2. Justification Section */}
        <div style={{ marginBottom: '1em' }}>
          <h6 style={{ marginBottom: '1em', color: '#48465b' }}>2. Justification (Required)</h6>

          <Form.Group className="required">
            <Form.Label>Reason</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Explain why this override is needed..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <Form.Text className="text-muted">
              This reason will be recorded in the audit log for compliance purposes.
            </Form.Text>
          </Form.Group>
        </div>

        {/* Warning box */}
        <div
          style={{
            background: '#fff3e0',
            padding: '1em',
            borderRadius: '4px',
            fontSize: '0.9em',
            color: '#e65100',
          }}
        >
          <strong>Warning:</strong> Overrides bypass role-based permissions. Use sparingly and ensure proper
          justification is provided.
        </div>
      </Form>
    </GenericModal>
  )
}

export default MerchantOverrideModal
